/**
 * Service API Backend
 *
 * Communique avec le backend Node.js (qui communique avec Odoo via XML-RPC)
 * Remplace odooApi.js pour une architecture plus sécurisée
 */

import axios from 'axios';
import BACKEND_URL from '../config/backendUrl';
import { getIntercomDeviceId, isIntercomTvDashboard } from '../utils/intercomDevice';
import { readAuthStorage, removeAuthStorage, writeAuthStorage } from '../utils/authStorage';

const AUTH_TOKEN_STORAGE_KEY = 'odoo_auth_token';

const readAuthToken = () => {
  return readAuthStorage(AUTH_TOKEN_STORAGE_KEY);
};

const tokenFromRequest = config => {
  const authorization = config?.headers?.Authorization || config?.headers?.authorization || '';
  const match = String(authorization).match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
};

export const shouldExpireAuthSession = (error, currentToken = readAuthToken()) => {
  if (error.response?.status !== 401) return false;
  if (String(error.config?.url || '').includes('/api/auth/login')) return false;
  const failedToken = tokenFromRequest(error.config);
  return currentToken ? failedToken === currentToken : !failedToken;
};

class BackendAPI {
  constructor() {
    this.baseURL = BACKEND_URL;
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 60000, // marge pour le retry/backoff XML-RPC côté backend (vue globale 79 projets)
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true // Important pour les cookies de session
    });

    this.client.interceptors.request.use((config) => {
      const token = readAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    // Intercepteur pour gérer les erreurs d'authentification
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (shouldExpireAuthSession(error)) {
          error.authSessionExpired = true;
          // Session expirée : nettoyer et notifier via event (pas de hard redirect)
          // Une ancienne requête ne doit jamais effacer un jeton créé plus récemment.
          removeAuthStorage('odoo_user');
          removeAuthStorage(AUTH_TOKEN_STORAGE_KEY);
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        return Promise.reject(error);
      }
    );

    console.log('🔗 Backend API Service initialisé:', this.baseURL);
  }

  /**
   * Authentifie un utilisateur
   * @param {string} username - Nom d'utilisateur Odoo
   * @param {string} password - Mot de passe Odoo
   * @returns {Promise<Object>}
   */
  async login(username, password) {
    try {
      const response = await this.client.post('/api/auth/login', {
        username,
        password
      });

      if (response.data.token) writeAuthStorage(AUTH_TOKEN_STORAGE_KEY, response.data.token);

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Erreur de connexion'
      );
    }
  }

  getAuthToken() {
    return readAuthToken();
  }

  /**
   * Déconnecte l'utilisateur
   */
  async logout() {
    try {
      await this.client.post('/api/auth/logout');
    } catch (error) {
      console.error('Erreur logout:', error);
    } finally {
      removeAuthStorage(AUTH_TOKEN_STORAGE_KEY);
    }
  }

  /**
   * Récupère les informations de l'utilisateur connecté
   * @returns {Promise<Object>}
   */
  async getCurrentUser() {
    try {
      const response = await this.client.get('/api/auth/me');
      return response.data.user;
    } catch (error) {
      throw new Error('Non authentifié');
    }
  }

  /**
   * Récupère la vue globale analytics (internes uniquement)
   * @returns {Promise<Object>} { ticketsByProject, milestones }
   */
  async getDevPerformance(sprintActif = false, projectId = null) {
    try {
      const params = {};
      if (sprintActif) params.sprintActif = 'true';
      if (projectId) params.projectId = projectId;
      const response = await this.client.get('/api/analytics/dev-performance', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur dev performance');
    }
  }

  async getSprintBurndown() {
    try {
      const response = await this.client.get('/api/analytics/sprint-burndown');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur sprint burndown');
    }
  }

  async getTeamPerformance(scope = 'sprint', projectId = null) {
    try {
      const params = { scope };
      if (projectId) params.projectId = projectId;
      const response = await this.client.get('/api/analytics/team-performance', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur performance équipe');
    }
  }

  async getCollaboratorRings(period = 'sprint', anchorDate, projectId = null) {
    try {
      const params = { period, anchorDate };
      if (projectId) params.projectId = projectId;
      const response = await this.client.get('/api/analytics/collaborator-rings', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur anneaux collaborateurs');
    }
  }

  async getTeamTvDashboard() {
    try {
      const response = await this.client.get('/api/team-tv');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur dashboard équipe');
    }
  }

  async getTeamTvAnnouncements() {
    try {
      const response = await this.client.get('/api/team-tv/announcements');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur annonces équipe');
    }
  }

  async getIntercomCalls(channel = 'personal') {
    try {
      const response = await this.client.get('/api/intercom/calls', {
        params: { channel, deviceId: getIntercomDeviceId() },
      });
      return response.data.calls;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de charger les appels interphone');
    }
  }

  async markIntercomPresence() {
    try {
      await this.client.post('/api/intercom/presence', {
        deviceId: getIntercomDeviceId(),
        isTv: isIntercomTvDashboard(),
      });
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de signaler la présence');
    }
  }

  async callTeamMember(targetId) {
    try {
      const response = await this.client.post('/api/intercom/calls', {
        targetId,
        deviceId: getIntercomDeviceId(),
      });
      return response.data.call;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible d’envoyer l’appel');
    }
  }

  async getUnreadChatMessages() {
    try {
      const response = await this.client.get('/api/chat/unread');
      return response.data.unread;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de charger les messages non lus');
    }
  }

  async getChatConversation(memberId) {
    try {
      const response = await this.client.get(`/api/chat/conversations/${memberId}`);
      return response.data.messages;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de charger la conversation');
    }
  }

  async sendChatMessage(memberId, body) {
    try {
      const response = await this.client.post(`/api/chat/conversations/${memberId}/messages`, { body });
      return response.data.message;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible d’envoyer le message');
    }
  }

  async markChatConversationRead(memberId) {
    try {
      const response = await this.client.patch(`/api/chat/conversations/${memberId}/read`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de marquer la conversation comme lue');
    }
  }

  async getPomodoro() {
    try {
      const response = await this.client.get('/api/pomodoro');
      return response.data.pomodoro;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de charger le Pomodoro');
    }
  }

  async startPomodoro(durationMinutes) {
    try {
      const response = await this.client.post('/api/pomodoro/start', { durationMinutes });
      return response.data.pomodoro;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de démarrer le Pomodoro');
    }
  }

  async pausePomodoro() {
    try {
      const response = await this.client.post('/api/pomodoro/pause');
      return response.data.pomodoro;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de mettre le Pomodoro en pause');
    }
  }

  async resumePomodoro() {
    try {
      const response = await this.client.post('/api/pomodoro/resume');
      return response.data.pomodoro;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible de reprendre le Pomodoro');
    }
  }

  async stopPomodoro() {
    try {
      const response = await this.client.post('/api/pomodoro/stop');
      return response.data.pomodoro;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Impossible d\'arrêter le Pomodoro');
    }
  }

  async getAnalyticsOverview(sprintActif = false) {
    try {
      const response = await this.client.get('/api/analytics/overview', {
        params: sprintActif ? { sprintActif: 'true' } : {}
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur analytics overview');
    }
  }

  /**
   * Récupère les projets du client
   * @returns {Promise<Object>} { projects: Array }
   */
  async getProjects() {
    try {
      const response = await this.client.get('/api/projects');
      return response.data;
    } catch (error) {
      console.error('❌ Erreur getProjects:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur récupération projets'
      );
    }
  }

  /**
   * Récupère les détails d'un projet (tasks, milestones, epics)
   * @param {number} projectId - ID du projet
   * @returns {Promise<Object>}
   */
  async flushCache() {
    try {
      await this.client.post('/api/projects/flush-cache');
    } catch (error) {
      console.warn('⚠️ flushCache:', error.message);
    }
  }

  async flushAnalyticsCache() {
    try {
      await this.client.post('/api/analytics/flush-cache');
    } catch (error) {
      console.warn('⚠️ flushAnalyticsCache:', error.message);
    }
  }

  async getProjectDetails(projectId) {
    try {
      const response = await this.client.get(`/api/projects/${projectId}`);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.error;
      console.error(`❌ getProjectDetails(${projectId}) — HTTP ${status ?? 'no response'}:`, serverMsg ?? error.message);
      throw new Error(serverMsg || `Erreur récupération projet (HTTP ${status ?? 'réseau'})`);
    }
  }

  /**
   * Récupère les messages du chatter d'un ticket
   * @param {number} ticketId - ID du ticket
   * @returns {Promise<Object>} { messages: Array }
   */
  async getTicketMessages(ticketId, page = 1) {
    try {
      const response = await this.client.get(`/api/tickets/${ticketId}/messages`, { params: { page } });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur getTicketMessages:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur récupération messages'
      );
    }
  }

  async classifyTickets(projectId, ticketIds) {
    const response = await this.client.post('/api/tickets/classifications', { projectId, ticketIds }, {timeout:120000});
    return response.data;
  }

  async startTicketClassification(projectId, ticketIds) {
    const response = await this.client.post('/api/tickets/classifications/jobs', {projectId,ticketIds}, {timeout:120000});
    return response.data;
  }

  async getTicketClassification(jobId) {
    const response = await this.client.get(`/api/tickets/classifications/jobs/${jobId}`);
    return response.data;
  }

  async searchTicketsWithAI(projectId, query) {
    try {
      const response = await this.client.post('/api/tickets/search', {projectId,query}, {timeout:120000});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Recherche IA indisponible');
    }
  }

  async getTicketAttachments(ticketId) {
    try {
      const response = await this.client.get(`/api/tickets/${ticketId}/attachments`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur récupération pièces jointes'
      );
    }
  }

  async getTicketAttachment(ticketId, attachmentId) {
    try {
      const response = await this.client.get(`/api/tickets/${ticketId}/attachments/${attachmentId}`, { timeout: 120000 });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur ouverture pièce jointe'
      );
    }
  }

  /**
   * Crée un nouveau message dans le chatter d'un ticket
   * @param {number} ticketId - ID du ticket
   * @param {string} message - Corps du message
   * @returns {Promise<Object>} { messageId, success, message }
   */
  async createTicketMessage(ticketId, message) {
    try {
      const response = await this.client.post(`/api/tickets/${ticketId}/messages`, { message });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur createTicketMessage:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur envoi message'
      );
    }
  }

  /**
   * Récupère les messages du chatter d'une User Story
   * @param {number} usId - ID de la User Story
   * @returns {Promise<Object>} { messages: Array }
   */
  async getUserStoryMessages(usId, { page = 1, limit = 5 } = {}) {
    try {
      const response = await this.client.get(`/api/userstories/${usId}/messages`, { params: { page, limit } });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur getUserStoryMessages:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur récupération messages US'
      );
    }
  }

  async getUserStoryDetails(usId) {
    try {
      const response = await this.client.get(`/api/userstories/${usId}/details`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Impossible de charger cette User Story');
    }
  }

  async getAcceptanceCriteria(usId) {
    try {
      const response = await this.client.get(`/api/userstories/${usId}/criteria`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération des critères');
    }
  }

  async updateAcceptanceCriterion(usId, criterionId, values) {
    try {
      const response = await this.client.put(`/api/userstories/${usId}/criteria/${criterionId}`, values);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur mise à jour du critère');
    }
  }

  async validateAllAcceptanceCriteria(usId) {
    try {
      const response = await this.client.post(`/api/userstories/${usId}/criteria/validate-all`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur validation des critères');
    }
  }

  async getAcceptanceCriterionScreenshot(usId, criterionId) {
    try {
      const response = await this.client.get(`/api/userstories/${usId}/criteria/${criterionId}/screenshot`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération de la capture');
    }
  }

  /**
   * Publie un message dans le chatter d'une User Story
   * @param {number} usId - ID de la User Story
   * @param {string} message - Corps du message
   * @returns {Promise<Object>} { messageId, success, message }
   */
  async createUserStoryMessage(usId, message) {
    try {
      const response = await this.client.post(`/api/userstories/${usId}/messages`, { message });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur createUserStoryMessage:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur envoi message US'
      );
    }
  }

  /**
   * Récupère les équipes helpdesk
   * @returns {Promise<Object>} { teams: Array }
   */
  async getHelpdeskTeams() {
    try {
      const response = await this.client.get('/api/helpdesk/teams');
      return response.data;
    } catch (error) {
      console.error('❌ Erreur getHelpdeskTeams:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur récupération équipes'
      );
    }
  }

  /**
   * Crée un nouveau ticket helpdesk
   * @param {Object} ticketData - Données du ticket (name, description, priority, team_id, project_id)
   * @returns {Promise<Object>} { ticketId, success, message }
   */
  async createTicket(ticketData) {
    try {
      const response = await this.client.post('/api/tickets/', ticketData, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur createTicket:', error.response || error);
      const ticketError = new Error(
        error.response?.data?.error || error.message || 'Erreur création ticket'
      );
      ticketError.authSessionExpired = error.authSessionExpired === true;
      throw ticketError;
    }
  }

  async triageSupport(payload) {
    try {
      const response = await this.client.post('/api/support/triage', payload);
      return response.data;
    } catch (error) {
      const supportError = new Error(error.response?.data?.error || error.message || 'Erreur de qualification');
      supportError.authSessionExpired = error.authSessionExpired === true;
      throw supportError;
    }
  }

  async finalizeSupportTriage(payload) {
    try {
      const response = await this.client.post('/api/support/triage/finalize', payload, { timeout: 120000 });
      return response.data;
    } catch (error) {
      const supportError = new Error(error.response?.data?.error || error.message || 'Erreur de transmission au support');
      supportError.authSessionExpired = error.authSessionExpired === true;
      throw supportError;
    }
  }

  async reportSupportAnswer(ticketId, feedback) {
    const response = await this.client.post('/api/support/triage/feedback', { ticketId, feedback });
    return response.data;
  }

  /**
   * Met à jour le stage d'une task (User Story) avec feedback optionnel et pièces jointes
   * @param {number} taskId - ID de la task
   * @param {number} stageId - ID du nouveau stage (132 pour Done, 255 pour Refusé)
   * @param {string} feedback - Feedback du client (optionnel)
   * @param {Array} attachments - Pièces jointes en base64 (optionnel)
   * @returns {Promise<Object>} { success, taskId, stageId, feedbackAdded, message }
   */
  async updateTaskStage(taskId, stageId, feedback = null, attachments = []) {
    try {
      const payload = { stageId };
      if (feedback) {
        payload.feedback = feedback;
      }
      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }

      const response = await this.client.put(`/api/projects/tasks/${taskId}/stage`, payload, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur updateTaskStage:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur mise à jour stage'
      );
    }
  }

  /**
   * Clôture un ticket (change son stage vers "Cloturé" - ID 4)
   * @param {number} ticketId - ID du ticket
   * @returns {Promise<Object>}
   */
  async closeTicket(ticketId) {
    try {
      const response = await this.client.put(`/api/tickets/${ticketId}/close`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur closeTicket:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur clôture ticket'
      );
    }
  }

  /**
   * Récupère les champs affichables du modèle x_deploiement
   * @returns {Promise<Object>} { fields: Object }
   */
  async getDeploymentFields() {
    try {
      const response = await this.client.get('/api/deployments/fields');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération champs déploiements');
    }
  }

  /**
   * Récupère les enregistrements x_deploiement
   * @returns {Promise<Object>} { deployments: Array, fields: Object }
   */
  async getDeployments(projectId = null) {
    try {
      const response = await this.client.get('/api/deployments', { params: projectId ? { projectId } : {} });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération déploiements');
    }
  }

  async signLivraison(id, signatureBase64) {
    try {
      const response = await this.client.put(`/api/versions/${id}/sign`, { signature: signatureBase64 });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur signature livraison');
    }
  }

  async getLivraisons(projectId = null) {
    try {
      const params = projectId ? { projectId } : {};
      const response = await this.client.get('/api/versions', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération livraisons');
    }
  }

  async getDocumentationProjects(projectId = null) {
    try {
      const response = await this.client.get('/api/documentation', {
        params: projectId ? { projectId } : {},
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur récupération documentation');
    }
  }

  async getDocumentation(documentationId, { projectId = null, version = 'latest', kind = 'functional' } = {}) {
    try {
      const response = await this.client.get(`/api/documentation/${encodeURIComponent(documentationId)}`, {
        params: { projectId, version, kind },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur lecture documentation');
    }
  }

  getDocumentationAssetUrl(documentationId, assetPath, projectId = null) {
    const encodedPath = String(assetPath || '').split('/').filter(Boolean).map(encodeURIComponent).join('/');
    const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return `${this.baseURL}/api/documentation/${encodeURIComponent(documentationId)}/assets/${encodedPath}${query}`;
  }

  /**
   * Valide une User Story côté client (ajoute l'étiquette "Validé par le client" sur Odoo)
   * @param {number} taskId - ID de la tâche
   * @returns {Promise<Object>} { success, taskId, message }
   */
  async validateUserStory(taskId) {
    try {
      const response = await this.client.post(`/api/projects/tasks/${taskId}/validate`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur validateUserStory:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur validation US'
      );
    }
  }

  /**
   * Retire la validation d'une User Story (supprime l'étiquette "Validé par le client")
   * @param {number} taskId - ID de la tâche
   * @returns {Promise<Object>} { success, taskId, message }
   */
  async unvalidateUserStory(taskId) {
    try {
      const response = await this.client.delete(`/api/projects/tasks/${taskId}/validate`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur unvalidateUserStory:', error.response || error);
      throw new Error(
        error.response?.data?.error || error.message || 'Erreur dévalidation US'
      );
    }
  }

  async askChatbot(question, projectId) {
    try {
      const response = await this.client.post('/api/chatbot/ask', {
        question,
        project_id: String(projectId),
      }, { timeout: 120_000 });
      return response.data;
    } catch (error) {
      console.error('❌ Erreur askChatbot:', error.response || error);
      throw new Error(error.response?.data?.error || error.message || 'Erreur chatbot');
    }
  }

  async startDynamicsLive(payload) {
    try {
      const response = await this.client.post('/api/chatbot/live/session', {
        project_id: payload.projectId, page_context: payload.pageContext,
      }, { timeout: 30000 });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Connexion vocale indisponible.');
    }
  }

  async askDocumentationAssistant(payload) {
    try {
      const response = await this.client.post('/api/chatbot/ask', {
        question: payload.question,
        project_id: String(payload.projectId),
        documentation_id: payload.documentationId,
        version: payload.version,
        mode: payload.mode,
        history: payload.history || [],
        page_context: payload.pageContext,
      }, { timeout: 120_000 });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Erreur assistant documentaire');
    }
  }

  /**
   * Test de connexion au backend
   * @returns {Promise<Object>}
   */
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      throw new Error('Backend indisponible');
    }
  }
}

// Export singleton
const backendApi = new BackendAPI();
export default backendApi;
